import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useMedicines } from '../../hooks/useMedicines';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiEdit, FiTrash2, FiClock, FiMoreVertical } = FiIcons;

const MedicinesList = () => {
  const navigate = useNavigate();
  const { medicines, loading, deleteMedicine } = useMedicines();
  const [selectedMedicine, setSelectedMedicine] = useState(null);

  const getMedicineTypeIcon = (type) => {
    const icons = {
      tablet: FiIcons.FiCircle,
      capsule: FiIcons.FiCircle,
      liquid: FiIcons.FiDroplet,
      injection: FiIcons.FiActivity,
      drops: FiIcons.FiDroplet,
      cream: FiIcons.FiLayers,
      inhaler: FiIcons.FiWind,
      patch: FiIcons.FiSquare
    };
    return icons[type] || FiIcons.FiCircle;
  };

  const getFoodInstructionText = (instruction) => {
    const instructions = {
      before_food: 'Before food',
      after_food: 'After food',
      with_food: 'With food',
      empty_stomach: 'Empty stomach',
      anytime: 'Anytime'
    };
    return instructions[instruction] || instruction;
  };

  const handleDeleteMedicine = async (medicineId) => {
    if (window.confirm('Are you sure you want to delete this medicine?')) {
      try {
        await deleteMedicine(medicineId);
      } catch (error) {
        console.error('Error deleting medicine:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Medicines</h1>
          <p className="text-gray-600 mt-2">Manage your medicine schedule and dosages</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/medicines/add')}
          className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
        >
          <SafeIcon icon={FiPlus} />
          <span>Add Medicine</span>
        </motion.button>
      </div>

      {/* Medicines Grid */}
      {medicines.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <SafeIcon icon={FiIcons.FiPill} className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No medicines added yet</h3>
          <p className="text-gray-600 mb-6">Start by adding your first medicine to track your medication schedule</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/medicines/add')}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            Add Your First Medicine
          </motion.button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {medicines.map((medicine, index) => (
            <motion.div
              key={medicine.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-200"
            >
              {/* Medicine Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <SafeIcon 
                      icon={getMedicineTypeIcon(medicine.type)} 
                      className="text-blue-600 text-xl" 
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{medicine.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{medicine.type}</p>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() => setSelectedMedicine(selectedMedicine === medicine.id ? null : medicine.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiMoreVertical} className="text-gray-400" />
                  </button>
                  
                  {selectedMedicine === medicine.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute right-0 top-12 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[120px]"
                    >
                      <button
                        onClick={() => navigate(`/medicines/edit/${medicine.id}`)}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiEdit} className="text-sm" />
                        <span>Edit</span>
                      </button>
                      <button
                        onClick={() => handleDeleteMedicine(medicine.id)}
                        className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                        <span>Delete</span>
                      </button>
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Medicine Details */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Dosage</span>
                  <span className="font-medium text-gray-900">{medicine.dosage}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Frequency</span>
                  <span className="font-medium text-gray-900">
                    {medicine.frequency} time{medicine.frequency > 1 ? 's' : ''}/day
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Food</span>
                  <span className="font-medium text-gray-900 text-sm">
                    {getFoodInstructionText(medicine.food_instruction)}
                  </span>
                </div>

                {/* Timing */}
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-2 mb-2">
                    <SafeIcon icon={FiClock} className="text-gray-400 text-sm" />
                    <span className="text-sm text-gray-600">Daily Schedule</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {medicine.timing?.map((time, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full font-medium"
                      >
                        {time}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Start Date */}
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">Started</span>
                    <span className="text-gray-900">
                      {format(new Date(medicine.start_date), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MedicinesList;